import type { IBallFinal, IBfMetricsDisplayData, IStrikeZoneDecision, Spin3DFinal } from '@trackman/baseball-shared-components';
import { broadcastDataToBfMetricsDisplayModelMap } from '@trackman/baseball-shared-components';
import type { IPlayData } from 'interfaces/broadcast/IPlayData';
import { action, makeObservable, observable } from 'mobx';

interface IBallFinalWithStrikeZoneDecision extends IBallFinal {
  strikeZoneDecision?: IStrikeZoneDecision;
}

export class DisplayDataStore {
  @observable displayData: IBfMetricsDisplayData[] = [];

  @observable itemCounter = 0;

  @observable ballFinals: IBallFinalWithStrikeZoneDecision[] = [];

  @observable spin3dFinal?: Spin3DFinal;

  @observable lastStrikeZoneDecision?: IStrikeZoneDecision;

  @observable currentPlayId?: string;

  @observable playData: IPlayData = null;

  constructor() {
    makeObservable(this);
  }

  // Rebuilds the newest displayData row from the current play's raw data. Every action that changes play
  // data calls it in the same action, so displayData (which the strike zone tiles read) never lags
  // behind the raw data the other tiles read.
  private rebuildDisplayData = (): void => {
    if (!this.currentPlayId) {
      return;
    }
    const display = broadcastDataToBfMetricsDisplayModelMap(this.ballFinals, undefined, undefined, this.spin3dFinal);
    display.id = this.currentPlayId;

    const lastElem = this.displayData[0];

    if (this.lastStrikeZoneDecision && this.lastStrikeZoneDecision.playId === this.currentPlayId) {
      display.strikeZoneDecision = this.lastStrikeZoneDecision;
    }

    if (this.displayData.length === 0 || this.displayData[0].id !== this.currentPlayId) {
      display.index = this.itemCounter + 1;
      this.setItemCounter(this.itemCounter + 1);

      this.displayData = [display, ...this.displayData]; // if there is no row for current play, create it
      return;
    }

    display.index = lastElem.index;
    this.setItemCounter(lastElem.index);

    this.displayData = [display, ...this.displayData.slice(1)]; // if there is, overwrite it
  };

  @action handleNewPlay = (
    playId: string,
    ballFinals: IBallFinal[],
    spin3dFinal: Spin3DFinal | undefined,
    strikeZoneDecision: IStrikeZoneDecision | undefined,
    playData: IPlayData | undefined
  ) => {
    this.currentPlayId = playId;
    this.ballFinals = ballFinals;
    this.spin3dFinal = spin3dFinal;
    this.lastStrikeZoneDecision = strikeZoneDecision;
    this.playData = playData;
    this.rebuildDisplayData();
  };

  @action setBallFinals = (ballFinal: IBallFinal) => {
    this.ballFinals = [...this.ballFinals, ballFinal];
    this.rebuildDisplayData();
  };

  @action setSpin3dFinal = (spin3dFinal?: Spin3DFinal) => {
    this.spin3dFinal = spin3dFinal;
    this.rebuildDisplayData();
  };

  @action setLastStrikeZoneDecision = (strikeZoneDecision?: IStrikeZoneDecision) => {
    this.lastStrikeZoneDecision = strikeZoneDecision;

    if (strikeZoneDecision) {
      const existingPlay = this.displayData.at(-1);
      if (existingPlay?.playId === strikeZoneDecision.playId) {
        existingPlay.strikeZoneDecision = strikeZoneDecision;
      }
    }

    this.rebuildDisplayData();
  };

  @action setItemCounter = (value: number) => {
    this.itemCounter = value;
  };

  @action setCurrentPlayId = (playId: string) => {
    this.currentPlayId = playId;
  };

  @action setPlayData = (playData: IPlayData) => {
    this.playData = playData;
    this.rebuildDisplayData();
  };

  @action clearGame = () => {
    this.displayData = [];
    this.itemCounter = 0;
    this.ballFinals = [];
    this.spin3dFinal = undefined;
    this.lastStrikeZoneDecision = undefined;
    this.currentPlayId = undefined;
    this.playData = null;
  };
}
